/**
 * This callback is displayed as a global member.
 * @callback eventCallback
 * @param {object} event Event callback
 * @param {HTMLElement} target Event target
 */

/**
 * Properties for delegating events
 * @typedef {Object} DelegateParams
 * @property {HTMLElement} element - Element to attach the event listener to
 * @property {string} event Event to listen for
 * @property {string} selector Selector to listen for events on
 * @property {eventCallback} callback Event Callback Function
 */
 interface DelegateParams<K extends keyof HTMLElementEventMap, E extends HTMLElement> {
  element:HTMLElement|Document|string;
  selector:string;
  callback:(event:HTMLElementEventMap[K], target:E)=>any
  event?:K;
  events?:K[];
};


/**
 * Attaches an event listener to an ancestor node to listen for events on the
 * children nodes.
 *
 * @param {DelegateParams} params Information about the event listener
 * @returns {void} A function to remove the attached event listener
 */
export const delegate = <K extends keyof HTMLElementEventMap, E extends HTMLElement>(params:DelegateParams<K,E>) => {
  const { element, selector, event, callback, events } = params;

  const internalCallback = (event:HTMLElementEventMap[K]) => {
    let target = findTarget(event, selector) as E;
    if(!target) return;
    callback(event, target);
  };

  let elem = (
    typeof element === 'string' ? document.querySelector<E>(element) :
    (element as Document).documentElement ? (element as Document).documentElement :
    element
  ) as E;
  if(!elem) return null;

  const arrEvents:K[] = [];
  if(event) arrEvents.push(event);
  if(events) arrEvents.push(...events);

  arrEvents.map(evt => elem.addEventListener(evt, internalCallback));

  return () => {
    arrEvents.forEach(evt => {
      elem && elem.removeEventListener(evt, internalCallback);
    });
  };
};

/**
 *
 * @param {Event} e - Event to find target from
 * @param {string} selector - Selector string to find target with
 */
export const findTarget = <E extends HTMLElement|SVGElement>(e:Event, selector:string) => {
  if(!e.target ||
    !(e.target instanceof HTMLElement || e.target instanceof SVGElement) ||
    typeof e.target['matches'] === typeof undefined
  ) return;

  const target = e.target.matches(selector) ? e.target as E : (
    e.target.closest<E>(selector)
  );
  return target;
}